import { inject, Injectable } from '@angular/core';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  from,
  interval,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timer
} from 'rxjs';

import { ApiOptions } from '@shared/models';
import { CustomToastrService, SpinnerService } from '@shared/services';
import { PocketbaseService } from '../pocketbase.service';
import { AuthService } from '../auth.service';
import { Created_Case } from '../../../utils/interfaces';

@Injectable({
  providedIn: 'root'
})
export class CaseApiService {
  private readonly pocketbaseService = inject(PocketbaseService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly customToastrService = inject(CustomToastrService);
  private readonly authService = inject(AuthService);

  readonly pb = this.pocketbaseService.getPocketBase();

  getCase(id: string, options?: ApiOptions) {
    return from(this.pb.collection('cases').getOne(id))
      .pipe(
        catchError((error) => {
          if (!options?.withoutErrorMessage) {
            this.customToastrService.showError(options?.errorMessage ?? (error?.message || 'Es ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'))
          }

          return throwError(() => new Error(error));
        }),
        finalize(() => options?.showSpinner && this.spinnerService.stopSpinner())
      );
  }

  getCasesCategories(options?: ApiOptions) {
    return from(this.pb.collection('cases').getFullList<{ category: string; }>({ sort: 'category', fields: 'category' }))
      .pipe(
        map(records => [...new Set(records.map(r => r.category))] as string[])
      );
  }

  /**
   * Load the full public case pool with expanded taxonomy relations.
   * Used by the catalog filter system (client-side filtering).
   */
  getPublicCasesWithTaxonomy(options?: ApiOptions) {
    return from(this.pb.collection('cases').getFullList({
      batch: 200,
      filter: 'organization:length = 0 && is_public = true',
      sort: '-created',
      expand: 'study_level_tags,legal_area_tags,legal_field_tags,topic_tags'
    })).pipe(catchError(() => from(Promise.resolve([] as any[]))));
  }

  /**
   * Load all active case taxonomy tags (studienstufe / rechtsbereich / rechtsgebiet / thema).
   */
  getCaseTags(options?: ApiOptions) {
    return from(this.pb.collection('case_tags').getFullList({
      batch: 300,
      filter: 'is_active = true',
      sort: 'sort_order,name'
    })).pipe(catchError(() => from(Promise.resolve([] as any[]))));
  }

  getCasesByCategory(page: number, perPage: number, category: string, options?: ApiOptions & { showPublicOnly?: boolean; showMyCases?: boolean; searchQuery?: string }) {
    const filterParts: string[] = [
      this.pb.filter('category = {:category}', { category })
    ];

    // If showPublicOnly, filter for public cases only
    if (options?.showPublicOnly) {
      filterParts.push('is_public = true');
    }

    // If showMyCases (for non-admin users), filter for user's own private cases
    if (options?.showMyCases && !this.authService.isAdmin()) {
      const userId = this.authService.user()?.['id'] || this.authService.user()?.id;
      filterParts.push(this.pb.filter('owner = {:userId} && is_public = false', { userId }));
    }

    // Add multi-field search if searchQuery is provided
    if (options?.searchQuery && options.searchQuery.trim()) {
      const q = options.searchQuery.trim();
      filterParts.push(this.pb.filter('(name~{:q}||description~{:q}||tags~{:q}||situation~{:q})', { q }));
    }

    const filter = filterParts.join(' && ');

    return from(this.pb.collection('cases')
      .getList(
        page,
        perPage,
        {
          filter,
          sort: '-created'
        }
      )
    )
      .pipe(catchError(() => EMPTY));
  }

  /**
   * Search cases for suggestions dropdown (not catalog filtering)
   * Returns flat list of cases matching search query
   */
  searchCases(query: string, limit = 10, options?: ApiOptions & { showPublicOnly?: boolean; showMyCases?: boolean }) {
    if (!query || !query.trim()) {
      return from(Promise.resolve([]));
    }

    const q = query.trim();
    const filterParts: string[] = [
      this.pb.filter('(name~{:q}||description~{:q}||tags~{:q}||situation~{:q})', { q })
    ];

    // Add visibility filter
    if (options?.showPublicOnly) {
      filterParts.push('is_public = true');
    }

    if (options?.showMyCases && !this.authService.isAdmin()) {
      const userId = this.authService.user()?.['id'] || this.authService.user()?.id;
      filterParts.push(this.pb.filter('owner = {:userId} && is_public = false', { userId }));
    }

    const filter = filterParts.join(' && ');

    return from(this.pb.collection('cases')
      .getList(1, limit, {
        filter,
        sort: '-created'
      })
    ).pipe(
      map(result => result?.items || []),
      catchError(() => from(Promise.resolve([])))
    );
  }

  updateCase(id: string, data: Created_Case, options?: ApiOptions) {
    const TIMEOUT_MS = 4 * 60 * 1000;

    if (options?.showSpinner) {
      this.spinnerService.startSpinner(options?.spinnerName);
    }

    const processed = {
      "name": data.processed?.name,
      "sample_solution": data.processed?.sample_solution,
      "conf_tab0": data.processed?.conf_tab0,
      "conf_tab1": data.processed?.conf_tab1,
      "conf_tab2": data.processed?.conf_tab2,
      "conf_tab3": data.processed?.conf_tab3,
      "conf_tab4": data.processed?.conf_tab4
    };

    return from(this.pb.collection('cases').getOne(id))
      .pipe(
        filter(caseToUpdate => !!caseToUpdate),
        switchMap(caseToUpdate => {
          const originalTags = caseToUpdate['tags'];
          const originalImg = caseToUpdate['img'];
          const processedId = caseToUpdate['processed'];

          const updated_case: Record<string, any> = {
            "name": data.name,
            "description": data.description,
            "category": data.category,
            "situation": data.situation,
            "processed": data.processed_id,
            "tags": data.tags ?? originalTags
          };

          // Only update img if explicitly provided (undefined means no change, null handled separately)
          if (data.img !== undefined) {
            updated_case["img"] = data.img ?? originalImg;
          }

          console.log('🔧 [updateCase] Updating case with:', {
            id,
            hasTags: !!updated_case["tags"],
            tags: updated_case["tags"],
            imgUpdated: data.img !== undefined,
            imgValue: data.img,
            originalImg
          });

          return from(this.pb.collection('cases_processed').update(processedId as string, processed)).pipe(
            map(() => ({ processedId, updated_case }))
          );
        }),
        switchMap(({ processedId, updated_case }) => {
          const status = options?.['status'] || '-1';
          return from(this.pb.collection('cases').update(id, { ...updated_case, processed: processedId, status: status }))
        }),
        switchMap(() =>
          interval(6000)
            .pipe(
              switchMap(() => this.getCase(id)),
              takeUntil(timer(TIMEOUT_MS)),
              filter((caseData: any) => Number(caseData?.status) > 0),
              tap((caseData: any) => {
                if (Number(caseData?.status) > 0) {
                  options?.showSuccessMessage && this.customToastrService.showSuccess(options?.successMessage ?? 'Erfolgreich aktualisiert')

                  throw new Error('FINISHED');
                }
              }),
              catchError((err) => {
                if (err.message === 'FINISHED') {
                  return EMPTY;
                }
                throw err;
              })
            )
        ),
        catchError((error) => {
          if (error !== EMPTY && !options?.withoutErrorMessage) {
            this.customToastrService.showError(options?.errorMessage ?? (error?.message || 'Es ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'))
          }

          return throwError(() => new Error(error));
        }),
        finalize(() => options?.showSpinner && this.spinnerService.stopSpinner(options?.spinnerName))
      )
  }

  // Note: Chunk confirmation is done via direct PocketBase update to cases_processed
  // (chunk_review_status: 'confirmed'), not via HTTP endpoint

  createCase(data: Created_Case, options?: ApiOptions) {
    if (options?.showSpinner) this.spinnerService.startSpinner(options.spinnerName);
    // 1) create processed record
    const processed = {
      "name": data.processed?.name,
      "sample_solution": data.processed?.sample_solution,
      "conf_tab0": data.processed?.conf_tab0,
      "conf_tab1": data.processed?.conf_tab1,
      "conf_tab2": data.processed?.conf_tab2,
      "conf_tab3": data.processed?.conf_tab3,
      "conf_tab4": data.processed?.conf_tab4
    };

    const user = this.authService.user();
    const userId = user?.['id'] || user?.id;
    const isAdmin = this.authService.isAdmin();
    // Author program: stamp author metadata so the case enters the partner pipeline as a draft.
    const authorFields = this.authService.isAuthor()
      ? { author: userId, publication_status: 'draft' }
      : {};

    return from(this.pb.collection('cases_processed').create(processed)).pipe(
      map(rec => rec.id),
      switchMap(processedId =>
        from(this.pb.collection('cases').create({
          ...data,
          ...authorFields,
          processed: processedId,
          status: '0',
          owner: userId,
          is_public: false
        })
        )),
      map(newCase => newCase.id),
      // 2) then poll until newCase.status > 0 just like updateCase does…
      switchMap(caseId => this.pollUntilProcessed(caseId, options)),
      map(newCase => newCase.id),
      finalize(() => options?.showSpinner && this.spinnerService.stopSpinner(options.spinnerName))
    );
  }

  createCaseNew(data: { name: string; pdf_musterloesung: File; description?: string; category?: string; situation?: string; sample_solution?: string; exam_duration?: number; tags?: string[]; img?: File }, options?: ApiOptions) {
    // Create processed record; include sample_solution if provided, otherwise blank as backend expects field
    const processedPayload: any = { name: data.name, sample_solution: data.sample_solution ?? ' ', pdf_musterloesung: data.pdf_musterloesung };

    const user = this.authService.user();
    const userId = user?.['id'] || user?.id;
    const isAdmin = this.authService.isAdmin();
    // Author program: stamp author metadata so the case enters the partner pipeline as a draft.
    const authorFields = this.authService.isAuthor()
      ? { author: userId, publication_status: 'draft' }
      : {};

    console.log('🏷️ [API Service] Creating case with tags:', data.tags);
    console.log('🖼️ [API Service] Creating case with img:', data.img?.name || 'No image');

    return from(this.pb.collection('cases_processed').create(processedPayload)).pipe(
      map(rec => rec.id),
      switchMap(processedId => {
        const casePayload = {
          ...authorFields,
          name: data.name,
          processed: processedId,
          // Pass through optional metadata if present; default to blanks to satisfy backend validators
          description: data.description ?? ' ',
          sample_solution: data.sample_solution ?? ' ',
          situation: data.situation ?? ' ',
          category: data.category ?? ' ',
          exam_duration: data.exam_duration,
          tags: data.tags,
          img: data.img,
          status: '0',
          owner: userId,
          is_public: false
        };

        console.log('📦 [API Service] PocketBase payload:', {
          tags: casePayload.tags,
          hasImg: !!casePayload.img,
          imgName: casePayload.img instanceof File ? casePayload.img.name : 'Not a file'
        });

        return from(this.pb.collection('cases').create(casePayload));
      }),
      // 2) then poll until newCase.status > 0 just like updateCase does…
      switchMap(response => {
        console.log('✅ [API Service] Case created in PocketBase:', {
          id: response.id,
          tags: response['tags'],
          img: response['img']
        });
        return this.pollUntilProcessed(response.id, options);
      }),
      finalize(() => options?.showSpinner && this.spinnerService.stopSpinner(options.spinnerName))
    );
  }

  deleteCase(caseId: string, options?: ApiOptions) {
    if (options?.showSpinner) {
      this.spinnerService.startSpinner(options.spinnerName);
    }

    return from(this.pb.collection('cases').delete(caseId))
      .pipe(
        tap(() => {
          if (options?.showSuccessMessage) {
            const msg = options.successMessage ?? 'Fall erfolgreich gelöscht';
            this.customToastrService.showSuccess(msg);
          }
        }),
        catchError((error) => {
          if (!options?.withoutErrorMessage) {
            this.customToastrService.showError(options?.errorMessage ?? 'Der Fall konnte nicht gelöscht werden. Bitte versuche es später noch einmal.')
          }

          return throwError(() => new Error(error));
        }),
        finalize(() => options?.showSpinner && this.spinnerService.stopSpinner(options?.spinnerName))
      );
  }

  private pollUntilProcessed(caseId: string, options?: ApiOptions) {
    return interval(5000).pipe(
      switchMap(() => this.getCase(caseId, { withoutErrorMessage: true })),
      tap(caseData => {
        console.log('🔄 [API Service] Polling case status:', {
          id: caseId,
          status: caseData['status'],
          tags: caseData['tags'],
          img: caseData['img']
        });

        // Check for processing error (status 99) - status may be string or number
        const status = Number(caseData['status']);
        if (status === 99) {
          console.error('❌ [API Service] Processing failed (status 99)');
          throw new Error('PDF_PARSING_ERROR');
        }
      }),
      filter(c => Number(c['status']) > 0),
      tap(() => {
        if (options?.showSuccessMessage) {
          const msg = options.successMessage ?? 'Erfolgreich erstellt';
          this.customToastrService.showSuccess(msg);
        }
      }),
      take(1),
      catchError(err => {
        if (err.message === 'PDF_PARSING_ERROR') {
          this.customToastrService.showError(
            'Die PDF-Datei konnte nicht verarbeitet werden. ' +
            'Bitte stellen Sie sicher, dass es sich um eine gültige PDF-Datei mit extrahierbarem Text handelt.'
          );
        }
        return throwError(() => err);
      })
    );
  }
}
